import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Check, X, Star, ArrowsClockwise, EnvelopeSimple, DownloadSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import Seo from '../components/Seo';

export default function Admin() {
  const { user, loading } = useAuth();
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [showSubs, setShowSubs] = useState(false);

  const refresh = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/admin/events/pending'),
        api.get('/admin/stats'),
      ]);
      setPending(p.data);
      setStats(s.data);
    } catch { /* ignore */ }
  };

  const loadSubscribers = async () => {
    try {
      const r = await api.get('/admin/subscribers');
      setSubscribers(r.data);
      setShowSubs(true);
    } catch { toast.error('Could not load subscribers'); }
  };

  const downloadSubscribersCsv = () => {
    if (!subscribers.length) return;
    const rows = [['email', 'source', 'created_at', 'unsubscribed']];
    subscribers.forEach(s => rows.push([s.email, s.source || '', s.created_at || '', s.unsubscribed ? 'yes' : 'no']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `powwowfinder-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const [digestSending, setDigestSending] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const previewDigest = async () => {
    setDigestSending(true);
    try {
      const r = await api.post('/admin/send-digest?dry_run=true');
      setDigestPreview(r.data);
    } catch (e) { toast.error('Could not preview digest'); }
    finally { setDigestSending(false); }
  };
  const sendDigest = async () => {
    if (!digestPreview) {
      toast.info('Click "Preview" first to confirm recipient count.');
      return;
    }
    const ok = window.confirm(`Send the weekly digest to ${digestPreview.total_recipients} recipients now? This cannot be undone.`);
    if (!ok) return;
    setDigestSending(true);
    try {
      const r = await api.post('/admin/send-digest?dry_run=false');
      if (r.data?.sent > 0 && r.data?.failed === 0) {
        toast.success(`Digest sent to ${r.data.sent} recipient${r.data.sent === 1 ? '' : 's'} ✉️`);
      } else if (r.data?.failed > 0) {
        toast.warning(`Sent ${r.data.sent}, ${r.data.failed} failed. ${r.data.last_error?.includes('RESEND_API_KEY') ? 'Add RESEND_API_KEY to backend .env to enable real delivery.' : ''}`);
      } else {
        toast.info(`No recipients yet. ${r.data?.reason || ''}`);
      }
      setDigestPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setDigestSending(false); }
  };

  const [purging, setPurging] = useState(false);
  const purgeTestEvents = async () => {
    if (!window.confirm('Delete all leftover test events (TEST_*, Iter tribes, @example.com contacts)? This is safe and cannot delete real pow wows.')) return;
    setPurging(true);
    try {
      const r = await api.post('/admin/purge-test-events');
      toast.success(`Deleted ${r.data.deleted} test event${r.data.deleted === 1 ? '' : 's'}. Refreshing…`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  useEffect(() => { if (user?.role === 'admin') refresh(); }, [user]);

  if (loading) return (
    <>
      <Seo path="/admin" title="Admin" noindex />
      <div className="p-12">Loading…</div>
    </>
  );
  if (!user || user.role !== 'admin') {
    return (
      <>
        <Seo path="/admin" title="Admin" noindex />
        <div className="max-w-2xl mx-auto p-24 text-center"><h1 className="font-display text-3xl">Admin only</h1><p className="text-muted-foreground mt-2">You need admin privileges to view this page.</p></div>
      </>
    );
  }

  const approve = async (id) => { await api.post(`/admin/events/${id}/approve`); toast.success('Approved'); refresh(); };
  const reject = async (id) => { await api.post(`/admin/events/${id}/reject`); toast.success('Rejected'); refresh(); };

  const viewFlyer = async (id, filename) => {
    try {
      const r = await api.get(`/admin/events/${id}/flyer`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: r.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke after a minute (give browser time to load)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(`Could not load flyer: ${e.response?.data?.detail || e.message}`);
    }
  };

  const refreshScrape = async () => {
    if (refreshing) return;
    setRefreshing(true);
    toast.info('Scraping powwows.com — this may take 30–90 seconds…');
    try {
      const r = await api.post('/admin/refresh-events');
      toast.success(`Done — added ${r.data.inserted}, skipped ${r.data.skipped} duplicates, ${r.data.failed_geocode} failed geocode`);
      refresh();
    } catch (e) {
      toast.error('Refresh failed — check backend logs');
    } finally {
      setRefreshing(false);
    }
  };

  const lastRun = stats?.last_scrape?.last_run_at;
  const fmtRel = (iso) => {
    if (!iso) return 'never';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo path="/admin" title="Admin" noindex />
      <p className="label text-terracotta mb-2">Admin</p>
      <h1 className="font-display text-4xl mb-8">Moderation dashboard</h1>

      <div className="bg-card border border-border rounded-2xl p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="scrape-panel">
        <div>
          <p className="font-display text-xl">Auto-import from powwows.com</p>
          <p className="text-sm text-muted-foreground mt-1">
            Last run: <strong>{fmtRel(lastRun)}</strong>
            {stats?.last_scrape && <> · added {stats.last_scrape.inserted} · skipped {stats.last_scrape.skipped}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={purgeTestEvents} disabled={purging} variant="outline" className="rounded-full border-crimson/50 text-crimson hover:bg-crimson/10" data-testid="purge-test-btn">
            <X size={14} weight="bold" className="mr-1" />
            {purging ? 'Purging…' : 'Purge test events'}
          </Button>
          <Button onClick={refreshScrape} disabled={refreshing} className="bg-forest hover:bg-forest/90 text-bone rounded-full" data-testid="scrape-refresh-btn">
            <ArrowsClockwise size={16} weight="bold" className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </Button>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { l: 'Total events', v: stats.total_events, color: 'text-turquoise' },
              { l: 'Pending', v: stats.pending_events, color: 'text-crimson' },
              { l: 'Users (total)', v: stats.users, color: 'text-turquoise' },
              { l: 'Saves', v: stats.saved_events, color: 'text-turquoise' },
            ].map(s => (
              <div key={s.l} className="bg-card border border-border rounded-2xl p-6">
                <p className="label text-muted-foreground mb-2">{s.l}</p>
                <p className={`font-display text-4xl ${s.color}`}>{s.v}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { l: 'Subscribers', v: stats.subscribers ?? 0, sub: `${stats.subscribers_today ?? 0} today`, color: 'text-gold' },
              { l: 'New users (7d)', v: stats.users_last_7d ?? 0, sub: 'last 7 days', color: 'text-turquoise' },
              { l: 'Submissions (7d)', v: stats.submissions_last_7d ?? 0, sub: 'last 7 days', color: 'text-crimson' },
              { l: 'Approved live', v: stats.total_events, sub: 'pow wows', color: 'text-gold' },
            ].map(s => (
              <div key={s.l} className="bg-card border border-border rounded-2xl p-6" data-testid={`stat-${s.l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <p className="label text-muted-foreground mb-2">{s.l}</p>
                <p className={`font-display text-4xl ${s.color}`}>{s.v}</p>
                {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 mb-12" data-testid="subscribers-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="font-display text-xl flex items-center gap-2">
                  <EnvelopeSimple size={20} weight="duotone" className="text-gold" />
                  Email subscribers
                </p>
                <p className="text-sm text-muted-foreground mt-1">{stats.subscribers ?? 0} active subscribers · {stats.subscribers_today ?? 0} new today</p>
              </div>
              <div className="flex gap-2">
                {!showSubs ? (
                  <Button onClick={loadSubscribers} variant="outline" className="rounded-full" data-testid="subs-load-btn">
                    View list
                  </Button>
                ) : (
                  <Button onClick={downloadSubscribersCsv} disabled={!subscribers.length} className="bg-gold text-navy hover:bg-gold/90 rounded-full" data-testid="subs-csv-btn">
                    <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Export CSV
                  </Button>
                )}
              </div>
            </div>
            {showSubs && (
              <div className="border-t border-border mt-3 pt-3 max-h-72 overflow-auto">
                {subscribers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No subscribers yet. The newsletter popup auto-shows after 15s on any page.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground uppercase tracking-wider">
                      <tr><th className="text-left py-2">Email</th><th className="text-left py-2">Source</th><th className="text-left py-2">Joined</th></tr>
                    </thead>
                    <tbody>
                      {subscribers.map(s => (
                        <tr key={s.email} className="border-t border-border/40">
                          <td className="py-2"><a href={`mailto:${s.email}`} className="hover:text-turquoise">{s.email}</a></td>
                          <td className="py-2 text-muted-foreground capitalize">{s.source || '—'}</td>
                          <td className="py-2 text-muted-foreground text-xs">{s.created_at?.slice(0, 10) || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-2xl p-6 mb-12" data-testid="digest-panel">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="font-display text-xl flex items-center gap-2">
                  <PaperPlaneTilt size={20} weight="duotone" className="text-turquoise" />
                  Weekly digest
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Email the next 20 upcoming pow wows to your subscribers + opted-in users.
                  {digestPreview && (
                    <> · <strong className="text-bone">{digestPreview.total_recipients}</strong> total recipients ({digestPreview.user_recipients} users + {digestPreview.subscriber_recipients} subscribers, deduped) · <strong className="text-gold">{digestPreview.event_count}</strong> events</>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={previewDigest} disabled={digestSending} variant="outline" className="rounded-full" data-testid="digest-preview-btn">
                  {digestSending && !digestPreview ? 'Counting…' : 'Preview'}
                </Button>
                <Button onClick={sendDigest} disabled={digestSending || !digestPreview} className="bg-turquoise text-navy hover:bg-turquoise/90 rounded-full font-semibold" data-testid="digest-send-btn">
                  <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                  {digestSending ? 'Sending…' : 'Send now'}
                </Button>
              </div>
            </div>
            {digestPreview?.last_error?.includes('RESEND_API_KEY') && (
              <div className="bg-crimson/10 border border-crimson/30 text-crimson rounded-lg px-3 py-2 text-xs">
                ⚠️ Real email delivery is not yet enabled. Add <code className="bg-navy/40 px-1 rounded">RESEND_API_KEY</code> to <code className="bg-navy/40 px-1 rounded">/app/backend/.env</code> and restart the backend — then the next &ldquo;Send now&rdquo; click will deliver.
              </div>
            )}
            {!digestPreview && (
              <p className="text-xs text-muted-foreground">Click <strong>Preview</strong> to see recipient + event counts before sending.</p>
            )}
          </div>
        </>
      )}

      <h2 className="font-display text-2xl mb-4">Pending submissions</h2>
      {pending.length === 0 ? (
        <p className="text-muted-foreground">No pending events. 🌿</p>
      ) : (
        <div className="space-y-4">
          {pending.map(e => (
            <div key={e.event_id} className="bg-card border border-border rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`pending-${e.event_id}`}>
              <div className="flex-1">
                <p className="label text-terracotta">{e.tribe || 'No tribe specified'}</p>
                <h3 className="font-display text-xl">{e.name}</h3>
                <p className="text-sm text-muted-foreground">{e.city}, {e.state} · {e.start_date} – {e.end_date}</p>
                <p className="text-sm mt-2 line-clamp-2">{e.description}</p>
                <div className="flex flex-wrap gap-3 mt-3 text-xs">
                  {e.submitted_by_email && <span className="text-muted-foreground">📧 <a href={`mailto:${e.submitted_by_email}`} className="text-turquoise hover:underline">{e.submitted_by_email}</a></span>}
                  {e.contact_phone && <span className="text-muted-foreground">📞 {e.contact_phone}</span>}
                  {e.has_flyer && (
                    <button
                      type="button"
                      onClick={() => viewFlyer(e.event_id, e.flyer_filename)}
                      className="inline-flex items-center gap-1 bg-turquoise/15 text-turquoise hover:bg-turquoise/25 rounded-full px-3 py-1 font-semibold"
                      data-testid={`flyer-link-${e.event_id}`}
                    >
                      📎 View flyer ({e.flyer_filename || 'attachment'})
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => approve(e.event_id)} className="bg-forest hover:bg-forest/90 text-bone rounded-full" data-testid={`approve-${e.event_id}`}>
                  <Check size={16} className="mr-1" /> Approve
                </Button>
                <Button onClick={() => reject(e.event_id)} variant="outline" className="rounded-full" data-testid={`reject-${e.event_id}`}>
                  <X size={16} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
